import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Grid, Paper, Alert } from "@mui/material";
import { useMediaQuery, useTheme } from "@mui/material";
import logo from '../../Assets/imgs/logo.png';
import './AdminLogin.css';

const LOGIN_TIMEOUT_MS = 45000;

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [openAlert, setOpenAlert] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const navigate = useNavigate();
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmScreen = useMediaQuery(theme.breakpoints.down('sm'));


  const backendURL = process.env.REACT_APP_TWCHATBOT_DOMAINS;
  const validDomains = ['techwave.net', 'techwave.com'];

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setEmailError('');
    setOpenAlert(false);
  };
const handleSubmit = async (event) => {
  event.preventDefault();
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const domain = email.split('@')[1];

  // ✅ Validation
  if (!emailPattern.test(email)) {
    setEmailError('Please enter a valid email address');
    setOpenAlert(true);
    return;
  } else if (!validDomains.includes(domain.toLowerCase())) {
    setEmailError('Provided domain is not supported');
    setOpenAlert(true);
    return;
  } else if (!password) {
    setEmailError('Please enter your password');
    setOpenAlert(true);
    return;
  } else {
    setEmailError('');
    setOpenAlert(false);

    // Abort if the backend hangs (e.g. database unreachable)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS);

    try {
      setIsProcessing(true);

      const formData = new FormData();
      formData.append('email', email);
      formData.append('password', password);

      // 👉 Admin login endpoint
      const response = await fetch(`${backendURL}/internal/admin_login/`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
        },
        signal: controller.signal,
      });

      // Backend may return HTML or an empty body on failure
      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        data = {};
      }

      // Server-side failure: log details, show a friendly message instead of raw DB errors
      if (response.status >= 500) {
        console.error('Admin login server error:', response.status, data.message);
        setEmailError('Login service is temporarily unavailable. Please try again later or contact support.');
        setOpenAlert(true);
        return;
      }

      // ✅ Handle backend response
      if (data.status === 'force_password_change' && data.user) {
        localStorage.setItem('pending_user_id', data.user.user_id);
        localStorage.setItem('pending_email', data.user.email);
        navigate('/internal/change_password');
        return;
      }

      if (response.ok && data.status === 'success' && data.user) {
        // ✅ Save admin user info
        localStorage.setItem('sr_id', data.user.sr_id || '');
        localStorage.setItem('user_id', data.user.user_id);

        const extractedName = data.user.email.split('@')[0];
        onLogin({ fullname: extractedName, email: data.user.email });

        // ✅ Redirect to Admin Dashboard
        navigate('/internal/admin_dashboard');
        return;
      }

      // ❌ Handle invalid credentials or other backend messages
      setEmailError(data.message || 'Login failed');
      setOpenAlert(true);
    } catch (error) {
      console.error('Error logging in admin:', error);
      setEmailError(
        error.name === 'AbortError'
          ? 'Login request timed out. Please try again later.'
          : 'Unable to reach the server. Please check your connection and try again.'
      );
      setOpenAlert(true);
    } finally {
      clearTimeout(timeoutId);
      setIsProcessing(false);
    }
  }
};


  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning ';
    if (hour < 16) return 'Good Afternoon ';
    return 'Good Evening ';
  };
  const getLogoSize = () => {
    if (isXsScreen) return { width: '80px', height: '32px' };
    if (isSmScreen) return { width: '100px', height: '20px' };
    return { width: '100px', height: '20px' };
  };

  return (
    <div>
      <div className='login-form-container'>
        <Grid className="form-container">
          <Grid item xs={10} sm={8} md={6} lg={4}>
            <Paper elevation={0} style={{ textAlign: 'center', backgroundColor: 'transparent' }}>
              <p className='greeting'>{getGreeting()}</p>
              <p className='sub-greeting'>Admin Login</p>
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Email Address"
                  placeholder="Enter your Email Address"
                  fullWidth
                  size="small"
                  type="email"
                  value={email}
                  className='text-Field'
                  onChange={handleEmailChange}
                  error={!!emailError}
                  InputLabelProps={{
                    shrink: true,
                    style: { color: 'black', fontSize: '14px', fontFamily: 'Kumbh Sans', fontWeight: 500 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#002395' },
                      '&:hover fieldset': { borderColor: '#002395' },
                      '&.Mui-focused fieldset': { borderColor: '#002395' },
                    },
                  }}
                />
                <TextField
                  label="Password"
                  placeholder="Enter your Password"
                  fullWidth
                  size="small"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                    style: { color: 'black', fontSize: '14px', fontFamily: 'Kumbh Sans', fontWeight: 500 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#153052' },
                      '&:hover fieldset': { borderColor: '#153052' },
                      '&.Mui-focused fieldset': { borderColor: '#153052' },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  size="large"
                  className='custom-button'
                  type="submit"
                  disabled={!email || !password || isProcessing}
                  sx={{ marginTop: 2 }} 
                >
                  {isProcessing ? 'Logging in...' : 'Continue'}
                </Button>
                {openAlert && (
                  <Alert
                    onClose={() => setOpenAlert(false)}
                    severity="error"
                    sx={{ marginTop: 2 }}
                  >
                    {emailError || 'Invalid login credentials.'}
                  </Alert>
                )}
              </form>
            </Paper>
          </Grid>
        </Grid>
      </div>
      <div className="ai-partner">
        <span style={{ color: 'black' }}>AI Partner </span>
        <a href="https://techwave.net/" target="_blank" rel="noopener noreferrer" className="techwave-link">
          <img src={logo} alt="techwave-logo" style={getLogoSize()} />
        </a>
      </div>
    </div>
  );
};

export default AdminLogin;
